import React, {ReactNode} from 'react'
import {InnerMiniTable, InnerMiniTableBody, TableRowName} from '../../../../../styledComponents/adminPage/Table'
import { Box, FormLabel, TableRow, TableCell } from '@mui/material';
import {LeftRowFlexContainer} from '../../../../../styledComponents/FlexComponents'
import PPSelect, {OptionProps} from '../../../../shared/forms/PPSelect'
import {
    HOURS,
    HOURS_24,
    RESERVATION_TIME_UNIT_MINUTES,
    UNIT_OF_BUSINESS_SCHEDULE,
    UNIT_TIME,
    VALID_OR_NOT,
} from '../../../../../CodeMaster'
import PPCheckboxGroup from '../../../../shared/forms/PPCheckboxGroup'
import {Messages} from '../../../../../Messages'
import {addEmptyData, isNoSelect, valueOrNoSelected} from '../../../../../componentHelper/selectHelper'
import {BusinessScheduleSettings, BusinessScheduleSettingsAndState} from './BusinessScheduleInput'
import BusinessScheduleChargeInputForm from './BusinessScheduleChargeInputForm'
import {Nullable} from '../../../../../app/TypeHelpers'
import {numberOrNull} from '../../../../../stringHelper/stringHelpers'
import {BusinessScheduleCharge} from './BusinessScheduleChargeComponent'
import {isValidJoinPrevDaysSchedule} from './BusinessScheduleSettingsComponentHelper'
import {SelectChangeEvent} from '@mui/material/Select'

interface Props {
    data: BusinessScheduleSettingsAndState
    onChange?: (data: BusinessScheduleSettingsAndState) => void
}

export const EVERY_DAY_OF_THE_WEEK: OptionProps = {
    label: '曜日毎',
    value: '1',
}

class BusinessScheduleInputForm extends React.Component<Props> {

    initialData: BusinessScheduleSettings = {
        businessScheduleUnit: null,
        dateStraddle: VALID_OR_NOT.NOT,
        joinPrevDaysSchedule: VALID_OR_NOT.NOT,
        startTimeHour: null,
        startTimeMinute: null,
        endTimeHour: null,
        endTimeMinute: null,
        changedChargeApplyStartTime: null,
        changedChargeApplyStartTimeUnit: UNIT_TIME.HOUR,
        charge: {
            charge: null,
            chargeUnit: UNIT_TIME.MINUTES,
            chargeUnitTime: null,
            fixedCharge: false,
        },
        changedCharge: {
            charge: null,
            chargeUnit: UNIT_TIME.MINUTES,
            chargeUnitTime: null,
            fixedCharge: false,
        },
    }

    constructor(props: Readonly<Props>) {
        super(props)

        this.handleBusinessSchedulePatternChange = this.handleBusinessSchedulePatternChange.bind(this)
        this.handleDayOfWeekChange = this.handleDayOfWeekChange.bind(this)
        this.handleJoinPrevDaysSchedule = this.handleJoinPrevDaysSchedule.bind(this)
        this.handleDateStraddleChange = this.handleDateStraddleChange.bind(this)
        this.handleNormalEndTimeHoursChange = this.handleNormalEndTimeHoursChange.bind(this)
        this.handleChargeChanged = this.handleChargeChanged.bind(this)
        this.handleChangedChargeApplyStartTime = this.handleChangedChargeApplyStartTime.bind(this)
        this.handleChangedChargeApplyStartTimeUnit = this.handleChangedChargeApplyStartTimeUnit.bind(this)
        this.handleChangedChargeChanged = this.handleChangedChargeChanged.bind(this)
    }

    notifyChange(changed: object): void {
        if (!this.props.onChange) {
            return
        }
        this.props.onChange({
            ...this.props.data,
            ...changed,
        })
    }

    handleBusinessSchedulePatternChange(event: SelectChangeEvent<unknown>): void {
        if (`${event.target.value}` === EVERY_DAY_OF_THE_WEEK.value) {
            this.notifyChange({
                ...this.initialData,
                businessSchedulePattern: `${event.target.value}`,
                businessScheduleUnit: UNIT_OF_BUSINESS_SCHEDULE.SUNDAY,
                dayOfWeek: UNIT_OF_BUSINESS_SCHEDULE.SUNDAY.value,
            })
        } else {
            const value: string = `${event.target.value}`
            this.notifyChange({
                ...this.initialData,
                businessSchedulePattern: value,
                businessScheduleUnit: isNoSelect(value) ? null : UNIT_OF_BUSINESS_SCHEDULE.codeToEnumConstants(value),
            })
        }
    }

    handleDayOfWeekChange(event: SelectChangeEvent<unknown>): void {
        const value: string = `${event.target.value}`
        this.notifyChange({
            businessScheduleUnit: isNoSelect(value) ? null : UNIT_OF_BUSINESS_SCHEDULE.codeToEnumConstants(value),
            dayOfWeek: value,
        })
    }

    handleJoinPrevDaysSchedule(label: string, checked: boolean): void {
        this.notifyChange({
            joinPrevDaysSchedule: checked ? VALID_OR_NOT.VALID : VALID_OR_NOT.NOT,
            startTimeHour: HOURS.VOL_0,
            startTimeMinute: RESERVATION_TIME_UNIT_MINUTES.VOL_0,
        })
    }

    handleDateStraddleChange(label: string, checked: boolean): void {
        this.notifyChange({
            dateStraddle: checked ? VALID_OR_NOT.VALID : VALID_OR_NOT.NOT,
            endTimeHour: null,
            endTimeMinute: null,
        })
    }

    handleMinuteValueChange = (minuteKey: string) => (event: SelectChangeEvent<unknown>) => {
        const value: string = `${event.target.value}`
        const minuteValue: Nullable<RESERVATION_TIME_UNIT_MINUTES> = isNoSelect(value) ? null :
            RESERVATION_TIME_UNIT_MINUTES.codeToEnumConstants(value)
        this.notifyChange({[minuteKey]: minuteValue})
    }

    handleHourValueChange = (hourKey: string) => (event: SelectChangeEvent<unknown>) => {
        const value: string = `${event.target.value}`
        const hourValue: Nullable<HOURS> = isNoSelect(value) ? null : HOURS.codeToEnumConstants(value)
        this.notifyChange({[hourKey]: hourValue})
    }

    handleNormalEndTimeHoursChange(event: SelectChangeEvent<unknown>): void {
        const value: string = `${event.target.value}`
        const hourValue: Nullable<HOURS_24> = isNoSelect(value) ? null : HOURS_24.codeToEnumConstants(value)
        if (hourValue === HOURS_24.VOL_24) {
            this.notifyChange({
                endTimeHour: hourValue,
                endTimeMinute: RESERVATION_TIME_UNIT_MINUTES.VOL_0,
            })
        } else {
            this.notifyChange({endTimeHour: hourValue})
        }
    }

    handleChargeChanged(businessScheduleCharge: BusinessScheduleCharge): void {
        this.notifyChange({
            charge: businessScheduleCharge,
        })
    }

    handleChangedChargeApplyStartTime(event: React.ChangeEvent<{ value: unknown }> | SelectChangeEvent<unknown>): void {
        const changedChargeApplyStartTime: Nullable<number> = numberOrNull(`${event.target.value}`)
        this.notifyChange({
            changedChargeApplyStartTime: changedChargeApplyStartTime,
        })
    }

    handleChangedChargeApplyStartTimeUnit(event: SelectChangeEvent<unknown>): void {
        const changedChargeApplyStartTimeUnit = UNIT_TIME.codeToEnumConstants(`${event.target.value}`)
        this.notifyChange({
            changedChargeApplyStartTime: null,
            changedChargeApplyStartTimeUnit: changedChargeApplyStartTimeUnit,
        })
    }

    handleChangedChargeChanged(businessScheduleCharge: BusinessScheduleCharge): void {
        this.notifyChange({
            changedCharge: businessScheduleCharge,
        })
    }

    renderSelectDayOfWeek(): ReactNode {
        if (this.props.data.businessSchedulePattern !== EVERY_DAY_OF_THE_WEEK.value) {
            return null
        }

        return (
            <TableRow>
                <TableRowName>曜日</TableRowName>
                <TableCell>
                    <LeftRowFlexContainer>
                        <PPSelect
                            data={[
                                UNIT_OF_BUSINESS_SCHEDULE.SUNDAY,
                                UNIT_OF_BUSINESS_SCHEDULE.MONDAY,
                                UNIT_OF_BUSINESS_SCHEDULE.TUESDAY,
                                UNIT_OF_BUSINESS_SCHEDULE.WEDNESDAY,
                                UNIT_OF_BUSINESS_SCHEDULE.THURSDAY,
                                UNIT_OF_BUSINESS_SCHEDULE.FRIDAY,
                                UNIT_OF_BUSINESS_SCHEDULE.SATURDAY,
                                UNIT_OF_BUSINESS_SCHEDULE.HOLIDAY,
                            ]}
                            value={this.props.data.dayOfWeek}
                            onChange={this.handleDayOfWeekChange}
                        />
                    </LeftRowFlexContainer>
                </TableCell>
            </TableRow>
        )
    }

    renderReserveAvailableTime(): ReactNode {
        const needToDisplay: boolean = [
            EVERY_DAY_OF_THE_WEEK.value,
            UNIT_OF_BUSINESS_SCHEDULE.ALL_DAYS.value,
        ].includes(this.props.data.businessSchedulePattern)

        if (!needToDisplay) {
            return null
        }

        const notHolidaySchedule: boolean = this.props.data.businessScheduleUnit !== UNIT_OF_BUSINESS_SCHEDULE.HOLIDAY
        const joinPrevDaysSchedule: boolean = this.props.data.joinPrevDaysSchedule === VALID_OR_NOT.VALID
        const dateStraddle: boolean = this.props.data.dateStraddle === VALID_OR_NOT.VALID
        return (
            <TableRow>
                <TableRowName>予約可能時間</TableRowName>
                <TableCell>
                    {
                        this.props.data.businessSchedulePattern === EVERY_DAY_OF_THE_WEEK.value &&
                        notHolidaySchedule &&
                        <PPCheckboxGroup
                            data={[
                                {
                                    label: '前日の営業スケジュールと連結する（日またぎ設定）',
                                    checked: joinPrevDaysSchedule,
                                },
                            ]}
                            onChangeChecked={this.handleJoinPrevDaysSchedule}
                        />
                    }
                    {
                        this.props.data.businessSchedulePattern === UNIT_OF_BUSINESS_SCHEDULE.ALL_DAYS.value &&
                        <PPCheckboxGroup
                            data={[
                                {
                                    label: '日をまたいで作成する',
                                    checked: dateStraddle,
                                },
                            ]}
                            onChangeChecked={this.handleDateStraddleChange}
                        />
                    }
                    {
                        dateStraddle
                            ? this.renderReserveAvailableTimeForStraddle()
                            : this.renderReserveAvailableTimeForNormal()
                    }
                </TableCell>
            </TableRow>
        )
    }

    renderReserveAvailableTimeForStraddle(): ReactNode {
        return (<>
            <LeftRowFlexContainer>
                <label>
                    <LeftRowFlexContainer>
                        <PPSelect
                            data={addEmptyData(HOURS.values())}
                            value={valueOrNoSelected(this.props.data.startTimeHour)}
                            onChange={this.handleHourValueChange('startTimeHour')}
                        />
                        時
                    </LeftRowFlexContainer>
                </label>
                <label>
                    <LeftRowFlexContainer>
                        <PPSelect
                            data={addEmptyData(RESERVATION_TIME_UNIT_MINUTES.values())}
                            value={valueOrNoSelected(this.props.data.startTimeMinute)}
                            onChange={this.handleMinuteValueChange('startTimeMinute')}
                        />
                        分 〜
                    </LeftRowFlexContainer>
                </label>
                <FormLabel disabled>
                    <LeftRowFlexContainer>
                        <PPSelect
                            data={HOURS_24.values()}
                            defaultValue={HOURS_24.VOL_24.value}
                            disabled
                        />
                        時
                    </LeftRowFlexContainer>
                </FormLabel>
                <FormLabel disabled>
                    <LeftRowFlexContainer>
                        <PPSelect
                            data={RESERVATION_TIME_UNIT_MINUTES.values()}
                            defaultValue={RESERVATION_TIME_UNIT_MINUTES.VOL_0.value}
                            disabled
                        />
                        分
                    </LeftRowFlexContainer>
                </FormLabel>
            </LeftRowFlexContainer>
            <LeftRowFlexContainer>
                <FormLabel disabled>
                    <LeftRowFlexContainer>
                        <PPSelect
                            data={HOURS.values()}
                            defaultValue={HOURS.VOL_0.value}
                            disabled
                        />
                        時
                    </LeftRowFlexContainer>
                </FormLabel>
                <FormLabel disabled>
                    <LeftRowFlexContainer>
                        <PPSelect
                            data={RESERVATION_TIME_UNIT_MINUTES.values()}
                            defaultValue={RESERVATION_TIME_UNIT_MINUTES.VOL_0.value}
                            disabled
                        />
                        分 〜
                    </LeftRowFlexContainer>
                </FormLabel>
                <label>
                    <LeftRowFlexContainer>
                        <PPSelect
                            data={addEmptyData(HOURS.values())}
                            value={valueOrNoSelected(this.props.data.endTimeHour)}
                            onChange={this.handleHourValueChange('endTimeHour')}
                        />
                        時
                    </LeftRowFlexContainer>
                </label>
                <label>
                    <LeftRowFlexContainer>
                        <PPSelect
                            data={addEmptyData(RESERVATION_TIME_UNIT_MINUTES.values())}
                            value={valueOrNoSelected(this.props.data.endTimeMinute)}
                            onChange={this.handleMinuteValueChange('endTimeMinute')}
                        />
                        分
                    </LeftRowFlexContainer>
                </label>
            </LeftRowFlexContainer>
        </>)
    }

    renderReserveAvailableTimeForNormal(): ReactNode {
        const {businessScheduleUnit, joinPrevDaysSchedule} = this.props.data
        const disableStartDateTime = businessScheduleUnit === null ||
            isValidJoinPrevDaysSchedule(joinPrevDaysSchedule, businessScheduleUnit)

        return (<>
            <LeftRowFlexContainer>
                <label>
                    <LeftRowFlexContainer>
                        <PPSelect
                            data={addEmptyData(HOURS.values())}
                            value={valueOrNoSelected(this.props.data.startTimeHour)}
                            disabled={disableStartDateTime}
                            onChange={this.handleHourValueChange('startTimeHour')}
                        />
                        時
                    </LeftRowFlexContainer>
                </label>
                <label>
                    <LeftRowFlexContainer>
                        <PPSelect
                            data={addEmptyData(RESERVATION_TIME_UNIT_MINUTES.values())}
                            value={valueOrNoSelected(this.props.data.startTimeMinute)}
                            disabled={disableStartDateTime}
                            onChange={this.handleMinuteValueChange('startTimeMinute')}
                        />
                        分 〜
                    </LeftRowFlexContainer>
                </label>
                <label>
                    <LeftRowFlexContainer>
                        <PPSelect
                            data={addEmptyData(HOURS_24.values())}
                            value={valueOrNoSelected(this.props.data.endTimeHour)}
                            onChange={this.handleNormalEndTimeHoursChange}
                        />
                        時
                    </LeftRowFlexContainer>
                </label>
                <label>
                    <LeftRowFlexContainer>
                        <PPSelect
                            data={addEmptyData(RESERVATION_TIME_UNIT_MINUTES.values())}
                            value={valueOrNoSelected(this.props.data.endTimeMinute)}
                            onChange={this.handleMinuteValueChange('endTimeMinute')}
                            disabled={
                                this.props.data.endTimeHour === HOURS_24.VOL_24
                            }
                        />
                        分
                    </LeftRowFlexContainer>
                </label>
            </LeftRowFlexContainer>
        </>)
    }

    renderPriceSetting(): ReactNode {
        const {
            charge,
            changedChargeApplyStartTime,
            changedChargeApplyStartTimeUnit,
            changedCharge,
            businessScheduleUnit,
            businessSchedulePattern,
            dayOfWeek,
            joinPrevDaysSchedule,
        } = this.props.data
        if (businessScheduleUnit === null || isValidJoinPrevDaysSchedule(joinPrevDaysSchedule, businessScheduleUnit)) {
            return null
        }

        return (<TableRow>
            <TableRowName>料金</TableRowName>
            <TableCell>
                <BusinessScheduleChargeInputForm
                    charge={charge}
                    changedChargeApplyStartTime={changedChargeApplyStartTime}
                    changedChargeApplyStartTimeUnit={changedChargeApplyStartTimeUnit}
                    changedCharge={changedCharge}
                    onChargeChanged={this.handleChargeChanged}
                    onChangedChargeChanged={this.handleChangedChargeChanged}
                    onChangedChargeApplyStartTime={this.handleChangedChargeApplyStartTime}
                    onChangedChargeApplyStartTimeUnit={this.handleChangedChargeApplyStartTimeUnit}
                    businessSchedulePattern={businessSchedulePattern}
                    dayOfWeek={dayOfWeek}
                />
            </TableCell>
        </TableRow>)
    }

    render(): ReactNode {
        return (<>
            <label>
                <LeftRowFlexContainer>
                    営業スケジュールパターン
                    <PPSelect
                        data={addEmptyData([
                            EVERY_DAY_OF_THE_WEEK,
                            UNIT_OF_BUSINESS_SCHEDULE.ALL_DAYS,
                            UNIT_OF_BUSINESS_SCHEDULE.DATE_STRADDLE,
                        ], '営業スケジュールパターンを選択してください')}
                        value={this.props.data.businessSchedulePattern}
                        onChange={this.handleBusinessSchedulePatternChange}
                        hint={Messages.HFACA0006.toString()}
                    />
                </LeftRowFlexContainer>
            </label>
            <Box marginTop={'15px'} marginBottom={'15px'}>
                <InnerMiniTable>
                    <InnerMiniTableBody>
                        {this.renderSelectDayOfWeek()}
                        {this.renderReserveAvailableTime()}
                        {this.renderPriceSetting()}
                    </InnerMiniTableBody>
                </InnerMiniTable>
            </Box>
        </>)
    }
}

export default BusinessScheduleInputForm
